import React, { useState } from "react";
import axios from "axios";
import { AlertCircle, X, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { usePlan } from "@/hooks/usePlan";
import { secureStorage } from "@/lib/secureStorage";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin) + "/api";

/**
 * PastDueBanner — fixed top banner that nudges past_due users to update their
 * payment method via the Paddle Customer Portal. Auto-hides if user dismisses
 * for the session.
 */
export const PastDueBanner = () => {
  const { isPastDue } = usePlan();
  const [dismissed, setDismissed] = useState(false);
  const [opening, setOpening] = useState(false);

  if (!isPastDue || dismissed) return null;

  const openPortal = async () => {
    setOpening(true);
    try {
      const token = secureStorage.getToken();
      const r = await axios.post(`${API}/billing/portal`, {}, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 12000,
      });
      if (r.data?.url) {
        window.open(r.data.url, "_blank", "noopener");
      } else {
        toast.error("No se pudo abrir el portal de gestión");
      }
    } catch (e) {
      toast.error("Portal de Paddle no disponible. Intenta más tarde.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-red-500/95 to-rose-600/95 backdrop-blur-md border-b border-red-400/50 px-4 py-2.5 flex items-center justify-center gap-3"
        data-testid="past-due-banner"
      >
        <AlertCircle className="w-4 h-4 text-white flex-shrink-0" />
        <p className="text-xs sm:text-sm text-white font-medium leading-snug">
          Tu pago no se procesó. Actualiza tu método de pago para mantener tu acceso.
        </p>
        <button
          onClick={openPortal}
          disabled={opening}
          className="px-3 py-1 rounded-md bg-white/20 hover:bg-white/30 text-white text-xs font-semibold transition-colors flex items-center gap-1.5 disabled:opacity-50"
          data-testid="past-due-fix"
        >
          {opening ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
          Actualizar pago
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="w-6 h-6 rounded hover:bg-white/15 flex items-center justify-center text-white/80 hover:text-white"
          aria-label="Cerrar"
          data-testid="past-due-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

export default PastDueBanner;
